import { useRef, type KeyboardEvent } from 'react';

interface TabListProps<K extends string> {
  tabs: { key: K; label: string }[];
  active: K;
  onChange: (key: K) => void;
  /** Shared with tabPanelProps so each tab and its panel reference each other. */
  idPrefix: string;
  className?: string;
  tabClassName?: string;
}

const tabId = (idPrefix: string, key: string) => `${idPrefix}-tab-${key}`;
const panelId = (idPrefix: string, key: string) => `${idPrefix}-panel-${key}`;

// A WAI-ARIA tablist: only the selected tab is in the Tab order, and the
// arrow keys, Home and End move focus and selection together.
export function TabList<K extends string>({
  tabs,
  active,
  onChange,
  idPrefix,
  className = '',
  tabClassName = '',
}: TabListProps<K>) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const current = tabs.findIndex((t) => t.key === active);
    const next = {
      ArrowRight: (current + 1) % tabs.length,
      ArrowLeft: (current - 1 + tabs.length) % tabs.length,
      Home: 0,
      End: tabs.length - 1,
    }[e.key];
    if (next === undefined) return;
    e.preventDefault();
    onChange(tabs[next].key);
    tabRefs.current[next]?.focus();
  };

  return (
    <div role="tablist" className={className} onKeyDown={handleKeyDown}>
      {tabs.map(({ key, label }, i) => (
        <button
          key={key}
          ref={(el) => {
            tabRefs.current[i] = el;
          }}
          type="button"
          role="tab"
          id={tabId(idPrefix, key)}
          aria-selected={active === key}
          aria-controls={panelId(idPrefix, key)}
          tabIndex={active === key ? 0 : -1}
          className={`tab ${tabClassName} ${active === key ? 'tab-active' : ''}`}
          onClick={() => onChange(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export function tabPanelProps(idPrefix: string, key: string) {
  return {
    role: 'tabpanel',
    id: panelId(idPrefix, key),
    'aria-labelledby': tabId(idPrefix, key),
  } as const;
}
